// src/pages/UserPage/sections/SenderReferralSection.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Link2,
  Copy,
  Check,
  Share2,
  Sparkles,
  Percent,
  UserPlus,
  GitBranch,
  RefreshCw,
} from 'lucide-react';
import { getOrCreateSenderReferral, getSenderReferralByCourier } from '../../../utils/sendShare';

// ─── EarningPill ──────────────────────────────────────────────────────────────

const EarningPill = ({ icon: Icon, label, color, bg }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1,
    px: 1.5, py: 1, borderRadius: '10px',
    bgcolor: bg, border: '1px solid', borderColor: alpha(color, 0.2),
    flex: 1, minWidth: 0,
  }}>
    <Box sx={{
      width: 28, height: 28, borderRadius: '8px', flexShrink: 0,
      bgcolor: alpha(color, 0.12),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon size={14} color={color} />
    </Box>
    <Typography variant="caption" fontWeight={600} color={color} sx={{ lineHeight: 1.3 }}>
      {label}
    </Typography>
  </Box>
);

// ─── SenderReferralSection ────────────────────────────────────────────────────

/**
 * Shows the courier's personal sender-referral link.
 * Placed inside UserPage below the existing profile sections.
 *
 * Props:
 *   user      – Firebase auth user
 *   userData  – Firestore user doc
 */
export const SenderReferralSection = ({ user, userData }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [shareId,    setShareId]    = useState(null);
  const [url,        setUrl]        = useState('');
  const [loading,    setLoading]    = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error,      setError]      = useState(null);
  const [copied,     setCopied]     = useState(false);

  // ── Load existing link on mount ───────────────────────────────────────────
  const loadLink = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const existing = await getSenderReferralByCourier(user.uid);
      if (existing) {
        setShareId(existing.shareId);
        setUrl(`${window.location.origin}/laheta/${existing.shareId}`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => { loadLink(); }, [loadLink]);

  // ── Generate new link ─────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const result = await getOrCreateSenderReferral(user.uid, {
        organizationId: userData?.organizationId ?? null,
      });
      setShareId(result.shareId);
      setUrl(result.url);
    } catch (err) {
      setError('Linkin luominen epäonnistui. Yritä uudelleen.');
    } finally {
      setGenerating(false);
    }
  };

  // ── Copy ──────────────────────────────────────────────────────────────────
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for non-secure contexts
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // ── Native share ──────────────────────────────────────────────────────────
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Lähetä paketti Pakettihuollolla',
          text: 'Lähetä paketti helposti — katso hinnat ja tilaa nouto:',
          url,
        });
      } catch { /* user cancelled */ }
    } else {
      handleCopy();
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  if (loading) return null; // silent load — no flash

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, borderTop: '1px solid', borderColor: 'divider' }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
            bgcolor: isDark ? '#0c2340' : '#eff6ff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid', borderColor: alpha('#2563eb', 0.2),
          }}>
            <Link2 size={18} color="#2563eb" strokeWidth={1.75} />
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>Oma lähetyslinkki</Typography>
            <Typography variant="caption" color="text.secondary">
              Jaa potentiaalisille asiakkaille
            </Typography>
          </Box>
        </Box>

        {shareId && (
          <Chip
            icon={<Sparkles size={12} />}
            label="Aktiivinen"
            size="small"
            sx={{
              height: 22, fontSize: '0.7rem', fontWeight: 700,
              bgcolor: isDark ? '#052e16' : '#f0fdf4',
              color: '#16a34a',
              border: '1px solid', borderColor: alpha('#16a34a', 0.25),
              '& .MuiChip-icon': { color: '#16a34a', ml: '6px' },
            }}
          />
        )}
      </Box>

      {/* ── Error ── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── No link yet ── */}
      {!shareId && (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          textAlign: 'center', gap: 2, py: 3,
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320, lineHeight: 1.65 }}>
            Luo henkilökohtainen linkki, jonka voit jakaa potentiaalisille lähettäjille.
            Jokaisesta linkin kautta tehdystä lähetyksestä saat asiakashankintapalkkion.
          </Typography>
          <Button
            variant="contained"
            startIcon={generating
              ? <CircularProgress size={16} color="inherit" />
              : <Link2 size={16} />}
            onClick={handleGenerate}
            disabled={generating}
            sx={{
              borderRadius: '10px', textTransform: 'none',
              fontWeight: 700, boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            Luo lähetyslinkki
          </Button>
        </Box>
      )}

      {/* ── Link display ── */}
      {shareId && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* URL row */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.5, py: 1.25,
            borderRadius: '12px',
            bgcolor: 'action.hover',
            border: '1px solid', borderColor: 'divider',
          }}>
            <Typography
              variant="body2"
              sx={{
                flex: 1, minWidth: 0,
                fontFamily: 'monospace', fontSize: '0.78rem',
                color: 'text.secondary',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {url}
            </Typography>

            <Tooltip title={copied ? 'Kopioitu!' : 'Kopioi linkki'} placement="top">
              <IconButton
                size="small"
                onClick={handleCopy}
                sx={{
                  borderRadius: '8px', flexShrink: 0,
                  color: copied ? '#16a34a' : 'text.secondary',
                  '&:hover': { bgcolor: 'action.selected' },
                  transition: 'color 0.2s',
                }}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </IconButton>
            </Tooltip>

            {'share' in navigator && (
              <Tooltip title="Jaa" placement="top">
                <IconButton
                  size="small"
                  onClick={handleShare}
                  sx={{
                    borderRadius: '8px', flexShrink: 0,
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                >
                  <Share2 size={16} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Earning pills */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <EarningPill
              icon={GitBranch}
              label="Asiakashankintapalkkio jokaisesta tilauksesta"
              color="#2563eb"
              bg={isDark ? '#172554' : '#eff6ff'}
            />
            <EarningPill
              icon={UserPlus}
              label="Referointipalkkio tai kokonaispalkkio, jos itse hoidat kuljetuksen"
              color="#0891b2"
              bg={isDark ? '#083344' : '#ecfeff'}
            />
          </Stack>

          <Divider />

          {/* Helper text */}
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Linkki ohjaa lähettäjän suoraan lähetystyökaluun. Kun lähetys tilataan,
            sinut merkitään löytäjäksi automaattisesti — sinun ei tarvitse tehdä mitään muuta.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SenderReferralSection;